/**
 * DataStore Service
 *
 * Handles all AWS Amplify API operations (no longer using DataStore.save)
 * Uses API.graphql() directly for reliability
 */

import { API } from "aws-amplify";
import { Game, GameSeries, Stone } from "../models";
import { logger } from "./debugLogger";
import { updateGame as updateGameMutation } from "../graphql/mutations";
import { getGame } from "../graphql/queries";

const LOG_CONTEXT = { component: "DataStoreService" };

/**
 * Update a game using API.graphql() directly
 *
 * NEW APPROACH - Uses API instead of DataStore for reliable cloud sync
 */
export const updateGameInDB = async (
  id: string,
  updates: Partial<Game>,
  setGame?: (g: Game) => void
): Promise<Game> => {
  logger.info(
    "Updating game via API",
    { ...LOG_CONTEXT, method: "updateGameInDB", gameId: id },
    { updates }
  );

  try {
    // First, get the current game to get its _version
    const getCurrentResponse: any = await API.graphql({
      query: getGame,
      variables: { id },
    });

    if (!getCurrentResponse?.data?.getGame) {
      logger.error("Game not found in DynamoDB", {
        ...LOG_CONTEXT,
        method: "updateGameInDB",
        gameId: id,
      });
      throw new Error(`Game with id ${id} not found`);
    }

    const currentGame = getCurrentResponse.data.getGame;
    logger.debug(
      "Current game fetched",
      { ...LOG_CONTEXT, method: "updateGameInDB", gameId: id },
      { version: currentGame._version }
    );

    // Prepare update input
    const updateInput: any = {
      id,
      _version: currentGame._version,
    };

    // Helper function to remove __typename from nested objects
    const removeTypename = (obj: any): any => {
      if (obj === null || obj === undefined) return obj;
      if (Array.isArray(obj)) {
        return obj.map((item) => removeTypename(item));
      }
      if (typeof obj === "object") {
        const newObj: any = {};
        for (const key in obj) {
          if (key !== "__typename") {
            newObj[key] = removeTypename(obj[key]);
          }
        }
        return newObj;
      }
      return obj;
    };

    // Copy only the fields that are being updated
    const allowedKeys = [
      "title",
      "description",
      "currentPlayer",
      "board",
      "currentStone",
      "player1",
      "player2",
      "player1UserId",
      "player2UserId",
      "gameHasStarted",
      "expertMode",
      "winner",
      "winRow",
      "iconSet",
      "stoneSetId",
      "seriesId",
      "rejoinedPlayers",
      "rematchCreated",
      "rematchId",
    ];

    for (const key of Object.keys(updates)) {
      if (allowedKeys.includes(key)) {
        const value = updates[key as keyof Game];
        // Skip undefined values - they shouldn't be sent to the API
        if (value === undefined) {
          logger.debug(`Skipping ${key} (undefined)`, {
            ...LOG_CONTEXT,
            method: "updateGameInDB",
            gameId: id,
          });
          continue;
        }
        // Remove __typename from nested objects (board, currentStone, etc.)
        const cleanedValue = removeTypename(value);
        updateInput[key] = cleanedValue;
        logger.debug(`Adding ${key} to update`, {
          ...LOG_CONTEXT,
          method: "updateGameInDB",
          gameId: id,
        });
        if (key === "iconSet") {
          console.log(
            "💾 Saving iconSet:",
            cleanedValue,
            "type:",
            typeof cleanedValue
          );
        }
      } else {
        logger.debug(`Skipping read-only or invalid key: ${key}`, {
          ...LOG_CONTEXT,
          method: "updateGameInDB",
          gameId: id,
        });
      }
    }

    // Update via API
    logger.debug("Calling API.graphql(updateGame)", {
      ...LOG_CONTEXT,
      method: "updateGameInDB",
      gameId: id,
    });

    // CRITICAL DEBUG: Log what we're sending (all fields)
    console.log("🔍 [updateGameInDB] Sending to API:", updateInput);

    const updateResponse: any = await API.graphql({
      query: updateGameMutation,
      variables: { input: updateInput },
    });

    if (!updateResponse?.data?.updateGame) {
      // Log detailed error information
      if (updateResponse?.errors) {
        logger.error(
          "API updateGame errors",
          { ...LOG_CONTEXT, method: "updateGameInDB", gameId: id },
          updateResponse.errors
        );
        console.error(
          "❌ [updateGameInDB] GraphQL Errors:",
          updateResponse.errors
        );
        throw new Error(
          `GraphQL Error: ${
            updateResponse.errors[0]?.message || "Unknown error"
          }`
        );
      }
      throw new Error("API updateGame returned no data");
    }

    const updated = updateResponse.data.updateGame;

    // CRITICAL DEBUG: Check if board is in response
    console.log("🔍 [updateGameInDB] API Response:", {
      id: updated.id,
      _version: updated._version,
      boardLength: updated.board?.length,
      board: updated.board,
      currentPlayer: updated.currentPlayer,
      currentStone: updated.currentStone,
    });

    logger.success(
      "Game updated via API!",
      { ...LOG_CONTEXT, method: "updateGameInDB", gameId: id },
      {
        updatedId: updated.id,
        newVersion: updated._version,
        boardLength: updated.board?.length,
      }
    );

    // Update React state if callback provided
    if (setGame) {
      logger.debug("Calling setGame callback", {
        ...LOG_CONTEXT,
        method: "updateGameInDB",
        gameId: id,
      });
      setGame(updated as Game);
    }

    return updated as Game;
  } catch (error: any) {
    logger.error(
      "Failed to update game via API",
      { ...LOG_CONTEXT, method: "updateGameInDB", gameId: id },
      error
    );
    logger.error(
      "Error details",
      { ...LOG_CONTEXT, method: "updateGameInDB", gameId: id },
      error.errors || error.message
    );
    throw error;
  }
};

/**
 * Query a game by ID via API
 * No longer using DataStore (sync disabled)
 */
export const getGameById = async (id: string): Promise<Game | undefined> => {
  logger.info("Fetching game from API", {
    ...LOG_CONTEXT,
    method: "getGameById",
    gameId: id,
  });

  console.log("🔍 [getGameById] FULL gameId:", id);
  console.log("🔍 [getGameById] gameId length:", id.length);
  console.log("🔍 [getGameById] gameId type:", typeof id);

  try {
    const { API } = await import("aws-amplify");
    const { getGame } = await import("../graphql/queries");

    console.log(
      "🔍 [getGameById] About to call API.graphql with query:",
      getGame
    );
    console.log("🔍 [getGameById] Variables being sent:", { id });

    const response: any = await API.graphql({
      query: getGame,
      variables: { id },
    });

    console.log("🔍 [getGameById] RAW API RESPONSE:", response);
    console.log("🔍 [getGameById] response.data:", response?.data);
    console.log(
      "🔍 [getGameById] response.data.getGame:",
      response?.data?.getGame
    );
    console.log("🔍 [getGameById] response.errors:", response?.errors);

    if (response?.data?.getGame) {
      const apiGame = response.data.getGame as Game;
      logger.success("Game found via API", {
        ...LOG_CONTEXT,
        method: "getGameById",
        gameId: id,
      });
      console.log("✅ [getGameById] Game found!", apiGame);
      return apiGame;
    } else {
      logger.warn("Game not found in API", {
        ...LOG_CONTEXT,
        method: "getGameById",
        gameId: id,
      });
      console.warn(
        "❌ [getGameById] Game NOT found in API. Response was:",
        response
      );
      return undefined;
    }
  } catch (error) {
    logger.error(
      "Failed to fetch game from API",
      { ...LOG_CONTEXT, method: "getGameById", gameId: id },
      error
    );
    console.error("💥 [getGameById] API ERROR:", error);
    return undefined;
  }
};

/**
 * Query all stones via API
 * No longer using DataStore (sync disabled)
 */
export const getAllStones = async (): Promise<Stone[]> => {
  logger.info("Fetching all stones from API", {
    ...LOG_CONTEXT,
    method: "getAllStones",
  });

  try {
    const { API } = await import("aws-amplify");
    const { listStones } = await import("../graphql/queries");

    const response: any = await API.graphql({
      query: listStones,
      variables: { limit: 100 },
    });

    if (response?.data?.listStones?.items) {
      const stones = response.data.listStones.items.filter(
        (s: any) => s !== null
      ) as Stone[];
      logger.success(`Fetched ${stones.length} stones from API`, {
        ...LOG_CONTEXT,
        method: "getAllStones",
      });
      return stones;
    } else {
      logger.warn("No stones found in API", {
        ...LOG_CONTEXT,
        method: "getAllStones",
      });
      return [];
    }
  } catch (error) {
    logger.error(
      "Failed to fetch stones from API",
      { ...LOG_CONTEXT, method: "getAllStones" },
      error
    );
    return [];
  }
};

/**
 * Query a game series by ID
 * Tries DataStore first, then falls back to API if not found
 */
export const getGameSeriesById = async (
  id: string
): Promise<GameSeries | undefined> => {
  logger.info(
    "Querying game series",
    { ...LOG_CONTEXT, method: "getGameSeriesById" },
    { seriesId: id }
  );

  try {
    const { API } = await import("aws-amplify");
    const { getGameSeries } = await import("../graphql/queries");

    const response: any = await API.graphql({
      query: getGameSeries,
      variables: { id },
    });

    if (response?.data?.getGameSeries) {
      const series = response.data.getGameSeries as GameSeries;
      logger.success("Game series found via API", {
        ...LOG_CONTEXT,
        method: "getGameSeriesById",
      });
      return series;
    } else {
      logger.warn("Game series not found in API", {
        ...LOG_CONTEXT,
        method: "getGameSeriesById",
      });
      return undefined;
    }
  } catch (error) {
    logger.error(
      "Failed to fetch series from API",
      { ...LOG_CONTEXT, method: "getGameSeriesById" },
      error
    );
    return undefined;
  }
};

/**
 * Subscribe to game changes
 */
export const subscribeToGame = (
  gameId: string,
  onUpdate: (game: Game) => void
) => {
  logger.info("🔔 Setting up GraphQL subscription for game updates", {
    ...LOG_CONTEXT,
    method: "subscribeToGame",
    gameId,
  });
  console.log("🔔 SUBSCRIPTION SETUP for game:", gameId);

  // Use GraphQL Subscription directly (more reliable than DataStore.observeQuery)
  const subscription = (
    API.graphql({
      query: `subscription OnUpdateGame($id: ID!) {
      onUpdateGame(filter: {id: {eq: $id}}) {
        id
        title
        description
        currentPlayer
        board {
          name
          key
          stone {
            name
            key
            color
            size
            shape
            variant
            img
            __typename
          }
          __typename
        }
        currentStone {
          name
          key
          color
          size
          shape
          variant
          img
        }
        player1
        player2
        gameHasStarted
        expertMode
        winner
        winRow
        iconSet
        stoneSetId
        seriesId
        rejoinedPlayers
        rematchCreated
        rematchId
        createdAt
        updatedAt
        _version
        _deleted
        _lastChangedAt
        gameSeriesGamesId
      }
    }`,
      variables: { id: gameId },
    }) as any
  ).subscribe({
    next: ({ value }: any) => {
      console.log("🔔 SUBSCRIPTION RAW DATA:", value);
      const updatedGame = value?.data?.onUpdateGame;
      if (updatedGame) {
        logger.info(
          "🔔 Game update received via subscription!",
          { ...LOG_CONTEXT, method: "subscribeToGame", gameId },
          {
            version: updatedGame._version,
            currentPlayer: updatedGame.currentPlayer,
            winner: updatedGame.winner,
          }
        );
        console.log("🔔 SUBSCRIPTION UPDATE:", updatedGame);
        onUpdate(updatedGame as Game);
      } else {
        console.warn("🔔 SUBSCRIPTION: No data in update", value);
      }
    },
    error: (error: any) => {
      logger.error(
        "❌ Subscription error",
        { ...LOG_CONTEXT, method: "subscribeToGame", gameId },
        error
      );
      console.error("❌ SUBSCRIPTION ERROR:", error);
    },
  });

  console.log("🔔 SUBSCRIPTION ACTIVE for game:", gameId);
  return subscription;
};

/**
 * Check DataStore sync status
 * NOTE: DataStore sync is disabled, this always returns false
 */
export const checkDataStoreSyncStatus = async (): Promise<boolean> => {
  logger.warn("DataStore sync is disabled, returning false", {
    ...LOG_CONTEXT,
    method: "checkDataStoreSyncStatus",
  });
  return false;
};

/**
 * Query multiple games by their IDs
 * Uses API.graphql to fetch games, since DataStore sync is disabled
 */
export const getGamesByIds = async (gameIds: string[]): Promise<Game[]> => {
  if (gameIds.length === 0) {
    return [];
  }

  logger.info("Fetching games by IDs", {
    ...LOG_CONTEXT,
    method: "getGamesByIds",
  });
  console.log("📦 [getGamesByIds] Fetching", gameIds.length, "games");

  try {
    const { API } = await import("aws-amplify");

    // Fetch all games in parallel
    const promises = gameIds.map(async (id) => {
      try {
        const response: any = await API.graphql({
          query: `query GetGame($id: ID!) {
            getGame(id: $id) {
              id
              title
              description
              currentPlayer
              board {
                name
                key
                stone {
                  name
                  key
                  color
                  size
                  shape
                  variant
                  img
                  __typename
                }
                __typename
              }
              currentStone {
                name
                key
                color
                size
                shape
                variant
                img
                __typename
              }
              player1
              player2
              gameHasStarted
              expertMode
              winner
              winRow
              iconSet
              stoneSetId
              seriesId
              rejoinedPlayers
              rematchCreated
              rematchId
              createdAt
              updatedAt
              _version
              _deleted
              _lastChangedAt
              gameSeriesGamesId
              __typename
            }
          }`,
          variables: { id },
        });

        if (response?.data?.getGame) {
          return response.data.getGame as Game;
        }
        return null;
      } catch (error) {
        console.error(`❌ Failed to fetch game ${id}:`, error);
        return null;
      }
    });

    const results = await Promise.all(promises);
    const games = results.filter((g): g is Game => g !== null);

    logger.success("Games fetched successfully", {
      ...LOG_CONTEXT,
      method: "getGamesByIds",
    });
    console.log(
      "✅ [getGamesByIds] Fetched",
      games.length,
      "of",
      gameIds.length,
      "games"
    );

    return games;
  } catch (error) {
    logger.error(
      "Failed to fetch games by IDs",
      { ...LOG_CONTEXT, method: "getGamesByIds" },
      error
    );
    return [];
  }
};

/**
 * Delete a game by ID via API
 */
export const deleteGameInDB = async (gameId: string): Promise<boolean> => {
  logger.info("Deleting game", {
    ...LOG_CONTEXT,
    method: "deleteGameInDB",
    gameId,
  });
  console.log("🗑️ [deleteGameInDB] Deleting game:", gameId);

  try {
    const { API } = await import("aws-amplify");

    // First, fetch the current game to get its _version
    const game = await getGameById(gameId);
    if (!game) {
      logger.warn("Game not found, cannot delete", {
        ...LOG_CONTEXT,
        method: "deleteGameInDB",
        gameId,
      });
      return false;
    }

    const response: any = await API.graphql({
      query: `mutation DeleteGame($input: DeleteGameInput!) {
        deleteGame(input: $input) {
          id
          _deleted
        }
      }`,
      variables: {
        input: {
          id: gameId,
          _version: (game as any)._version,
        },
      },
    });

    if (response?.data?.deleteGame) {
      logger.success("Game deleted successfully", {
        ...LOG_CONTEXT,
        method: "deleteGameInDB",
        gameId,
      });
      console.log("✅ [deleteGameInDB] Game deleted:", gameId);

      // Remove from localStorage and sync to cloud
      try {
        const { removeGameFromLocalStorage } = await import("../utils/common");
        await removeGameFromLocalStorage(gameId);
      } catch (error) {
        console.log("ℹ️ Could not remove from localStorage:", error);
      }

      return true;
    }

    return false;
  } catch (error) {
    logger.error(
      "Failed to delete game",
      { ...LOG_CONTEXT, method: "deleteGameInDB", gameId },
      error
    );
    return false;
  }
};
